// Global packages and components
import { useState, useRef, useEffect } from 'react'
import { hasActivePriceFilter } from '~/utils'

// Types
import type { BrendaSearchFilter, ProductListingPriceValues } from '~/types'

interface Props {
  priceLimit: BrendaSearchFilter | null
  appliedRange: string | null
  setQueryPriceRange: (value: string | null) => void
}

/*
  Price slider state.

  The bounds now arrive with the loader data on every render, so re-seeding has
  to happen during render rather than in an effect. An effect-driven seed writes
  the URL, which navigates, which returns the same facet, which re-seeds, which
  writes again. That loop is latent today and would become tight and guaranteed
  once the server supplies priceLimit on every render.

  Only a user's hand sets `touched`, and only `touched` authorises a URL write.
*/
const usePriceFilter = ({
  priceLimit,
  appliedRange,
  setQueryPriceRange,
}: Props) => {
  const [rangeStart, rangeEnd] = appliedRange?.split('-').map(Number) ?? []

  const seed = (): ProductListingPriceValues => ({
    min: priceLimit?.min ?? undefined,
    max: priceLimit?.max ?? undefined,
    start: rangeStart ?? priceLimit?.min ?? 0,
    end: rangeEnd ?? priceLimit?.max ?? 0,
  })

  const key = `${priceLimit?.min}|${priceLimit?.max}|${appliedRange ?? ''}`
  const [priceValues, setPriceValues] = useState<ProductListingPriceValues>(seed)
  const seededKey = useRef(key)
  const touched = useRef(false)

  // Render phase re-seed when the server bounds or the applied range move.
  // It cannot set `touched`, so it can never write the URL
  if (seededKey.current !== key) {
    seededKey.current = key
    touched.current = false

    setPriceValues(seed())
  }

  // Debounced write, gated on a real interaction
  useEffect(() => {
    if (!touched.current) return

    const queryDelay = setTimeout(() => {
      touched.current = false

      // Dragging back to the full range has to remove the param rather than
      // no-op, or the stale filter stays applied forever
      setQueryPriceRange(
        hasActivePriceFilter(priceValues)
          ? `${priceValues?.start}-${priceValues?.end}`
          : null
      )
    }, 500)

    return () => clearTimeout(queryDelay)
  }, [priceValues, setQueryPriceRange])

  // Only this path may write the URL
  const setUserPriceValues = (next: ProductListingPriceValues) => {
    touched.current = true

    setPriceValues(next)
  }

  return { priceValues, setPriceValues: setUserPriceValues }
}

export default usePriceFilter
